import { useQuery } from '@tanstack/react-query';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useEffect, useMemo, useRef } from 'react';
import { useApi } from '@/shared/api';
import { canViewGuestPii, useAuth } from '@/shared/auth';
import { useObjectScope } from '@/shared/object-scope';
import { safeTimeZone, todayInTimeZone } from '../../dashboard/operationsDate';
import { buildPropertyLabels, labelFor } from '../../properties/propertyLabels';
import {
  DEFAULT_PAGE_SIZE,
  defaultSort,
  EMPTY_FILTERS,
  filtersFromSearch,
  isFiltered,
  searchFromState,
  sortFromSearch,
  toServerSort,
  type BookingsFilterState,
  type BookingsSearch,
} from '../components/bookingsListSearch';
import { bookingAgenda, flatAgenda } from './agendaModel';
import { sortRowsLocally } from './agendaSort';

/**
 * Zustand von „Aktuell & kommend“: URL-Parameter, Abfragen der Liste und der Apartments und die
 * Abschnitte daraus. Heute ist der Tag in der Zeitzone des gewählten Objekts, ohne Objekt die des
 * Property-Standards.
 */
export function useBookingsList() {
  const api = useApi();
  const navigate = useNavigate();
  const { group, propertyIds } = useObjectScope();
  const search = useSearch({ strict: false }) as BookingsSearch;
  const canSeeGuests = canViewGuestPii(useAuth().user);
  const today = todayInTimeZone(safeTimeZone(group?.timezone));
  const filters = filtersFromSearch(search);
  const requested = sortFromSearch(search);
  // Ohne Gastdaten gibt es keine Gast-Sortierung; ein geteilter Link fällt auf die Standardreihenfolge.
  const sort =
    canSeeGuests || requested.field !== 'guest'
      ? requested
      : defaultSort(filtersFromSearch(search).showPast);
  const page = search.page ?? 1;
  const pageSize = search.size ?? DEFAULT_PAGE_SIZE;
  const query = search.q ?? '';
  const scopeKey = propertyIds?.join(',') ?? 'all';

  const apply = (patch: Partial<Parameters<typeof searchFromState>[0]>) =>
    void navigate({
      to: '/bookings',
      search: searchFromState({ filters, sort, page, pageSize, query, ...patch }) as never,
    });
  // Wer „Vergangene zeigen“ umlegt, bekommt die Standardsortierung der neuen Ansicht mit.
  const setFilters = (next: BookingsFilterState) => {
    const old = defaultSort(filters.showPast);
    const keepSort = sort.field !== old.field || sort.direction !== old.direction;
    apply({ filters: next, page: 1, sort: keepSort ? sort : defaultSort(next.showPast) });
  };

  // Wechselt der Objekt-Scope, kann die aktuelle Seite im neuen Bestand leer sein. Der erste Lauf
  // bleibt aus: ein geteilter Link mit `?page=3` soll auf Seite 3 landen.
  const knownScope = useRef<string | null>(null);
  useEffect(() => {
    const previous = knownScope.current;
    knownScope.current = scopeKey;
    if (previous === null || previous === scopeKey) return;
    if (page > 1) apply({ page: 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeKey]);

  const queryArgs = {
    page,
    pageSize,
    sort: toServerSort(sort, filters.showPast),
    status: filters.status === 'all' ? undefined : filters.status,
    dateFrom: filters.dateFrom || undefined,
    dateTo: filters.dateTo || undefined,
    checkOutFrom: filters.showPast ? undefined : today,
    propertyId: filters.propertyId || undefined,
    propertyIds: propertyIds ?? undefined,
    search: query || undefined,
  };
  const list = useQuery({
    queryKey: ['bookings', 'list', queryArgs],
    queryFn: () => api.bookings.list(queryArgs),
  });
  // Auch archivierte Apartments: die Liste kann ihre Buchungen enthalten (G3-Entscheid), ohne sie
  // fiele die Namensauflösung auf eine abgeschnittene GUID zurück.
  const properties = useQuery({
    queryKey: ['properties', 'list', 'all-for-filter', propertyIds],
    queryFn: () =>
      api.properties.list({
        pageSize: 200,
        propertyIds: propertyIds ?? undefined,
        lifecycle: 'All',
      }),
    staleTime: 5 * 60 * 1000,
  });
  const propertyNames = useMemo(
    () => buildPropertyLabels(properties.data?.items ?? []),
    [properties.data],
  );
  const data = list.data;
  const rows = sortRowsLocally(data?.items ?? [], sort, (id) => labelFor(propertyNames, id).name);
  const sections = sort.field === 'checkInDate' ? bookingAgenda(rows, today) : flatAgenda(rows);
  const filtered = isFiltered(filters, query);
  const reset = () =>
    apply({ filters: EMPTY_FILTERS, query: '', page: 1, sort: defaultSort(false) });

  return {
    canSeeGuests,
    today,
    filters,
    sort,
    page,
    pageSize,
    query,
    apply,
    setFilters,
    list,
    properties,
    propertyNames,
    sections,
    filtered,
    reset,
  };
}
